// DOM normalization helpers. The contenteditable's "shape" — the
// invariants the markdown serializer relies on — must hold after
// any mutation: paste, drop, programmatic insert, etc. Browsers
// freely produce things like nested `<p>` elements after a paste,
// which the serializer collapses to inline text (losing newlines,
// which in turn keeps the composer's `hasNewLine` flag false and
// breaks the pill-vs-rounded-rect switch).
//
// This module owns the post-paste cleanup pass.

const BLOCK_TAGS: ReadonlySet<string> = new Set([
  'P',
  'DIV',
  'BLOCKQUOTE',
  'PRE',
  'UL',
  'OL',
]);

function isBlockTag(tag: string): boolean {
  return BLOCK_TAGS.has(tag);
}

/**
 * Top-level normalizer. Runs the cleanup passes in order:
 *   1. Promote nested block elements to root siblings.
 *   2. Coalesce stray text nodes at the root into `<p>` wrappers.
 *   3. Drop empty `<p>` runs left behind by the previous steps.
 *   4. Guarantee the root has at least one block child so the caret
 *      always has somewhere to land.
 */
export function normalizeBlocks(root: HTMLElement): void {
  promoteNestedBlocks(root);
  wrapStrayInlineRootChildren(root);
  dropMalformedCodeBlocks(root);
  collapseEmptyBlocks(root);
  ensureAtLeastOneBlock(root);
}

/**
 * Remove a `<pre>` that has lost its `<code>` element — a malformed,
 * chip-only zombie the browser can leave behind when a delete-all removes the
 * `<code>` but not the `contenteditable="false"` language chip. Such a `<pre>`
 * can't be typed into, serializes its chip label into the message body, and
 * jams the code-block toggle. A well-formed code block always keeps its
 * `<code>` (even when empty, it carries a `<br>` placeholder), so this only
 * ever fires on genuine garbage.
 */
function dropMalformedCodeBlocks(root: HTMLElement): void {
  const pres = Array.from(root.querySelectorAll('pre'));
  for (const pre of pres) {
    if (pre.querySelector('code') === null) {
      pre.parentNode?.removeChild(pre);
    }
  }
}

/**
 * Walk the tree finding a block element nested inside another
 * block element; bubble it up one level. Repeat until every block
 * sits as a direct child of `root`. Each iteration mutates the
 * DOM, so we re-query each pass.
 */
function promoteNestedBlocks(root: HTMLElement): void {
  // Cap iterations defensively so a pathological tree can never
  // spin forever. 128 is well above any realistic paste depth.
  for (let pass = 0; pass < 128; pass++) {
    const nested = findNestedBlock(root);
    if (nested === null) return;
    const wrapper = nested.parentElement;
    if (wrapper === null || wrapper === root) return;
    promoteOneLevel(nested, wrapper);
  }
}

function findNestedBlock(root: HTMLElement): HTMLElement | null {
  // Find any block whose IMMEDIATE parent is another block (and
  // not the root). One-level checks are cheap and good enough —
  // the outer loop repeats until stable.
  const all = root.querySelectorAll<HTMLElement>('p, div, blockquote, pre');
  for (const el of Array.from(all)) {
    const parent = el.parentElement;
    if (parent === null || parent === root) continue;
    if (BLOCK_TAGS.has(parent.tagName)) return el;
  }
  return null;
}

/**
 * Promote `child` out of `wrapper` by splitting `wrapper`. The
 * "before" siblings stay in a clone of `wrapper` placed before
 * `child`; the "after" siblings move into a second clone placed
 * after `child`. `child` itself sits between the two clones.
 */
function promoteOneLevel(child: HTMLElement, wrapper: HTMLElement): void {
  const parent = wrapper.parentElement;
  if (parent === null) return;
  const doc = wrapper.ownerDocument;
  const tag = wrapper.tagName.toLowerCase();

  const beforeWrapper = doc.createElement(tag);
  while (wrapper.firstChild !== null && wrapper.firstChild !== child) {
    beforeWrapper.appendChild(wrapper.firstChild);
  }

  const afterWrapper = doc.createElement(tag);
  // Skip past `child` itself when collecting `after` content.
  let next = child.nextSibling;
  while (next !== null) {
    const sib = next;
    next = next.nextSibling;
    afterWrapper.appendChild(sib);
  }

  // Detach child from wrapper.
  wrapper.removeChild(child);

  if (beforeWrapper.childNodes.length > 0) {
    parent.insertBefore(beforeWrapper, wrapper);
  }
  parent.insertBefore(child, wrapper);
  if (afterWrapper.childNodes.length > 0) {
    parent.insertBefore(afterWrapper, wrapper);
  }
  parent.removeChild(wrapper);
}

/**
 * Any text node / inline element that ended up as a direct child of
 * the root gets wrapped in a `<p>` so the serializer doesn't drop it.
 * Consecutive inline nodes coalesce into one `<p>`.
 */
function wrapStrayInlineRootChildren(root: HTMLElement): void {
  const doc = root.ownerDocument;
  let cur = root.firstChild;
  let buffer: Node[] = [];
  const flush = (insertBefore: ChildNode | null) => {
    if (buffer.length === 0) return;
    const wrap = doc.createElement('p');
    for (const n of buffer) wrap.appendChild(n);
    buffer = [];
    root.insertBefore(wrap, insertBefore);
  };
  while (cur !== null) {
    const next = cur.nextSibling;
    const isBlock =
      cur.nodeType === Node.ELEMENT_NODE &&
      isBlockTag((cur as HTMLElement).tagName);
    if (isBlock) {
      flush(cur as ChildNode);
    } else {
      buffer.push(cur);
    }
    cur = next;
  }
  flush(null);
}

/**
 * Drop truly-empty `<p></p>` / `<div></div>` blocks left over from
 * browser HTML parsing (unmatched `</p>` auto-closers, etc.) and
 * from the promotion splits above. `<p><br></p>` is intentionally
 * preserved — that's a caret-landing placeholder for blank lines
 * BETWEEN content.
 */
function collapseEmptyBlocks(root: HTMLElement): void {
  const blocks = Array.from(root.children).filter((c) =>
    isBlockTag(c.tagName),
  ) as HTMLElement[];
  for (const b of blocks) {
    if (b.childNodes.length === 0) {
      b.parentNode?.removeChild(b);
    }
  }
}

/**
 * If the editor's root is empty, drop a placeholder `<p><br></p>`
 * in so the caret has something to anchor on.
 */
function ensureAtLeastOneBlock(root: HTMLElement): void {
  if (root.childNodes.length === 0) {
    const doc = root.ownerDocument;
    const p = doc.createElement('p');
    p.appendChild(doc.createElement('br'));
    root.appendChild(p);
  }
}
